import React, { useContext } from 'react';
import { dollarMask } from '../../core/util';
import AllocationInput from '../AllocationInput/AllocationInput';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import { CartContext } from '../../context/CartContext/CartContext';
import { checkIClassValidation, checkSummitIClassValidation } from './utils';
import selectFundsRibbon from '../../images/select-funds-ribbon.svg';
import milistone from '../../images/Summit-Circle.svg';
import warningIcon from '../../images/icon-warning.svg';
import unlockIcon from '../../images/blue-unlock.svg';
import styles from './styles.css';
import {
  I_CLASS_MINIMUM,
  addCommas,
  decideFundMinimumProperty,
  isNonFundedFlow,
  toThousands
} from '../../flows/shared/utils';
import { FlowContext } from '../../core/flow-engine';
import { checkSummitIClassEligibility } from '../../flows/shared/summit.utils';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

const renderModeBtn = (val, selectedFunds, active, onClick) =>
  active || !selectedFunds ? (
    val
  ) : (
    <a
      className="toggle-link"
      href="#"
      onClick={e => {
        e.preventDefault();
        onClick();
      }}>
      {val}
      <style jsx>{styles}</style>
    </a>
  );

const IClassMinimumValidation = ({ amount, iClassReward }) => {
  const displayMinimumWithCommas = addCommas(I_CLASS_MINIMUM);
  return iClassReward
    ? checkSummitIClassValidation(amount) && (
        <>
          <div role="alert" aria-atomic="true" className="validation-msg">
            <img
              className="validation-msg-icon"
              src={warningIcon}
              alt="warning"
            />
            A $50,000 minimum is required for I Class
          </div>
          <style jsx>{styles}</style>
        </>
      )
    : checkIClassValidation(amount) && (
        <>
          <div className="minimum-validation">
            <div
              data-name="minimumValidationError"
              role="alert"
              aria-atomic="true">
              Allocation does not meet the minimum investment amount of $
              {displayMinimumWithCommas} per fund.
            </div>
          </div>
          <style jsx>{styles}</style>
        </>
      );
};

const MinimumValidation = ({ accountType, meetsFundMinimum, fundMinimum }) => {
  const fundMinimumWithComma = addCommas(fundMinimum);
  return (
    <>
      <div className="minimum-validation">
        {meetsFundMinimum || (
          <div
            data-name="minimumValidationError"
            role="alert"
            aria-atomic="true">
            {isNonFundedFlow(accountType)
              ? `This fund requires a minimum investment of $${fundMinimumWithComma}.`
              : `Allocation does not meet the minimum investment amount of $${fundMinimumWithComma} per fund.`}
          </div>
        )}
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

// TODO @Abhi [2022-07-01]: fix complexity
/* eslint-disable complexity */
const AllocationTable = ({
  fundsWithCategories,
  readOnly,
  rewardsAvailable = { closedFunds: false, iClassFunds: false },
  isTaxable = false,
  handleAddMoreFundsClick = () => {}
}) => {
  const { appState } = useContext(FlowContext);
  const {
    store: { accountType }
  } = appState;
  const cartState = useContext(CartContext);
  const {
    percentMode,
    totalAmount,
    percentSum,
    amountSum,
    requiredTotalText,
    isValidTotal,
    api: { setPercentMode, removeFund, changeValue }
  } = cartState;

  const selectedFunds = fundsWithCategories.length;
  const checkisSummitIClassMinIsValid = (fund, iClassReward) =>
    fund.iClass && iClassReward && checkSummitIClassValidation(fund.amount);

  const dividendInfo = `This fund pays monthly dividends that will be deposited in your 
  bank account. If preferred, they may be distributed via check.`;

  return (
    <div className="allocation-table">
      <div className="headers">
        <span>Fund Name</span>
        <span>
          Allocation:{' '}
          <span className="amount-modes">
            {renderModeBtn('%', selectedFunds, percentMode, () =>
              setPercentMode(true)
            )}{' '}
            |{' '}
            {renderModeBtn('$', selectedFunds, !percentMode, () =>
              setPercentMode(false)
            )}
          </span>
        </span>
      </div>
      {checkSummitIClassEligibility(totalAmount, rewardsAvailable) && (
        <div className="iclass-funds-message-view">
          <img
            className="iclass-funds-message-img"
            src={milistone}
            alt="Select Fund Certified"
          />
          <span className="iclass-funds-message-content">
            You&apos;re eligible to invest in lower-cost I Class shares ($50,000
            fund minimum).
          </span>
        </div>
      )}
      <div className="funds">
        {fundsWithCategories.map(fund => {
          const {
            stocks,
            bonds,
            shortTermReserves,
            fundAllocationsPresent
          } = fund.allocation;
          const meetsFundMinimum = decideFundMinimumProperty(
            accountType,
            fund.meetsAccountMinimumOpenIRA,
            fund.meetsMinimumInvestmentAmount
          );
          const fundMinimum = decideFundMinimumProperty(
            accountType,
            fund.accountMinimumOpenIRA,
            fund.minimumInvestmentAmount
          );

          return (
            <div className="fund-row" key={fund.ticker}>
              <div className="upper-half-of-fund-row">
                <div className="fund-details">
                  <div className="name">
                    {fund.unlockedFund && (
                      <img
                        className="allo-sf-ribbon-icon"
                        src={unlockIcon}
                        alt="Unlocked"
                      />
                    )}
                    {fund.selectFund && (
                      <img
                        className="allo-sf-ribbon-icon"
                        src={selectFundsRibbon}
                        alt="Select Fund Certified"
                      />
                    )}
                    {fund.name} <span className="ticker">({fund.ticker})</span>
                  </div>

                  {fundAllocationsPresent && (
                    <div className="allocation">
                      <span className="icon stocks-icon" />
                      <span className="icon-description"> {stocks}% </span>
                      <span className="icon bonds-icon" />
                      <span className="icon-description"> {bonds}% </span>
                      <span className="icon short-term-icon" />
                      <span className="icon-description">
                        {' '}
                        {shortTermReserves}%{' '}
                      </span>
                    </div>
                  )}
                </div>
                {readOnly || (
                  <div className="remove-button">
                    <span className="remove-fund-btn">
                      <MFBeaconButton
                        type="button"
                        id="removeFundBtn"
                        onClick={() => removeFund(fund.ticker)}
                        variant="tertiary"
                        label="Remove"
                        data-name={generateGeneralTestID(
                          'Cart',
                          `RemoveFundButton_${fund.ticker}`
                        )}
                      />
                    </span>
                  </div>
                )}
                <AllocationInput
                  percentMode={percentMode}
                  onChange={value => changeValue(fund.ticker, value)}
                  percent={fund.percent}
                  dollarValue={fund.amount}
                  totalAmount={totalAmount}
                  readOnly={readOnly}
                  isSummitIClassMinValid={checkisSummitIClassMinIsValid(
                    fund,
                    rewardsAvailable.iClassFunds
                  )}
                />
              </div>
              {fund.retirementIncome && (
                <div className="dividend-info">{dividendInfo}</div>
              )}
              {fund.iClass ? (
                <IClassMinimumValidation
                  amount={fund.amount}
                  iClassReward={rewardsAvailable.iClassFunds}
                />
              ) : (
                <MinimumValidation
                  accountType={accountType}
                  meetsFundMinimum={meetsFundMinimum}
                  fundMinimum={fundMinimum}
                />
              )}
            </div>
          );
        })}{' '}
      </div>

      {selectedFunds ? (
        <div className="totals">
          <div className="totals-row">
            <div>
              {isNonFundedFlow(accountType)
                ? 'Expected Investment'
                : 'Total Amount'}{' '}
              ${dollarMask(totalAmount)}
            </div>

            {percentMode ? (
              <div className="totals-row-number">
                {toThousands(percentSum)}%
              </div>
            ) : (
              <div className="totals-row-number">${amountSum}</div>
            )}
          </div>

          {isValidTotal || (
            <div role="alert" aria-atomic="true" className="validation-msg">
              <img
                className="validation-msg-icon"
                src={warningIcon}
                alt="warning"
              />
              Your allocation must total {requiredTotalText}.{' '}
            </div>
          )}
        </div>
      ) : (
        <div className="no-funds">
          <div role="alert" aria-atomic="true">
            You must select an investment to open an account.
          </div>
        </div>
      )}

      {isTaxable && (
        <MFBeaconButton
          id="AddMoreFundsButton"
          data-name={generateGeneralTestID('Cart', 'AddMoreFundsButton')}
          onClick={handleAddMoreFundsClick}
          type="button"
          variant="tertiary"
          label="+ Add More Funds"
        />
      )}

      <style jsx>{styles}</style>
    </div>
  );
};
/* eslint-enable complexity */

AllocationTable.defaultProps = {
  readOnly: false
};

export default AllocationTable;
